import { useEffect, useState } from "react";
import { api, fmtDate, fmtDateTime } from "@/lib/api";
import { Key, Plus, Broadcast, Copy, X, Trash } from "@phosphor-icons/react";

export default function Developer() {
  const [tab, setTab] = useState("api");
  return (
    <div className="space-y-5" data-testid="developer-page">
      <div>
        <div className="label-eyebrow">Developer</div>
        <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">API Keys & Webhooks</h1>
        <div className="text-[12px] text-[var(--span-text-2)] mt-1">Integrate SPAN with your internal systems via REST + outbound webhooks.</div>
      </div>
      <div className="border-b border-[var(--span-border)] flex gap-6">
        {[{ id: "api", label: "API keys", Icon: Key }, { id: "wh", label: "Webhooks", Icon: Broadcast }].map(t => (
          <button key={t.id} data-testid={`dev-tab-${t.id}`} onClick={() => setTab(t.id)} className={`py-3 text-[12px] uppercase font-medium tracking-[0.16em] border-b-2 flex items-center gap-2 ${tab === t.id ? "border-black" : "border-transparent text-[var(--span-text-2)]"}`}>
            <t.Icon size={13}/> {t.label}
          </button>
        ))}
      </div>
      {tab === "api" ? <ApiKeysTab /> : <WebhooksTab />}
    </div>
  );
}

function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);

  const load = () => api.get("/api-keys").then(({ data }) => setKeys(data));
  useEffect(() => { load(); }, []);

  const create = async (name) => {
    const { data } = await api.post("/api-keys", { name, scopes: ["read", "write"] });
    setCreated(data);
    setOpen(false);
    load();
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this API key? Future requests using it will fail.")) return;
    await api.delete(`/api-keys/${id}`);
    load();
  };

  return (
    <>
      <div className="flex justify-end">
        <button data-testid="new-key-btn" onClick={() => setOpen(true)} className="span-btn"><Plus size={13}/> New API key</button>
      </div>
      {created && (
        <div className="card-flat p-4 mt-3 border-l-4" style={{ borderLeftColor: "var(--span-warning)" }} data-testid="new-key-banner">
          <div className="label-eyebrow flex items-center gap-1">⚠ Copy your key now — it will not be shown again</div>
          <div className="flex items-center gap-2 mt-2">
            <code className="font-mono text-[12px] bg-[var(--span-surface-2)] p-2 flex-1 break-all">{created.key}</code>
            <button onClick={() => { navigator.clipboard.writeText(created.key); }} className="span-btn span-btn-ghost h-8 text-[11px]"><Copy size={12}/> Copy</button>
            <button onClick={() => setCreated(null)} className="span-btn span-btn-ghost h-8 text-[11px]"><X size={12}/></button>
          </div>
        </div>
      )}
      <div className="card-flat mt-4">
        <table className="span-table">
          <thead><tr><th>Name</th><th>Prefix</th><th>Scopes</th><th>Created</th><th>Last used</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {keys.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-[12px] text-[var(--span-text-2)]">No API keys yet.</td></tr>}
            {keys.map(k => (
              <tr key={k.key_id}>
                <td>{k.name}</td>
                <td className="font-mono text-[11px]">{k.prefix}</td>
                <td>{(k.scopes || []).map(s => <span key={s} className="badge mr-1">{s}</span>)}</td>
                <td className="text-[var(--span-text-2)]">{fmtDate(k.created_at)}</td>
                <td className="text-[var(--span-text-2)]">{k.last_used ? fmtDate(k.last_used) : "never"}</td>
                <td><span className={`badge ${k.active ? "badge-success" : "badge-danger"}`}>{k.active ? "active" : "revoked"}</span></td>
                <td>{k.active && <button onClick={() => revoke(k.key_id)} className="span-btn span-btn-ghost h-7 text-[11px]"><Trash size={11}/> Revoke</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <NewKey onClose={() => setOpen(false)} onCreate={create} />}
    </>
  );
}

function NewKey({ onClose, onCreate }) {
  const [name, setName] = useState("");
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={(e) => { e.preventDefault(); onCreate(name); }} className="card-flat w-full max-w-[440px]">
        <div className="px-5 py-4 border-b border-[var(--span-border)] flex items-center justify-between">
          <div className="font-display font-bold text-[17px]">New API key</div>
          <button type="button" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="label-eyebrow block mb-1.5">Name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Bajaj IT integration" className="span-input" data-testid="key-name"/>
        </div>
        <div className="px-5 py-4 border-t border-[var(--span-border)] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
          <button className="span-btn" data-testid="submit-key">Generate key</button>
        </div>
      </form>
    </div>
  );
}

function WebhooksTab() {
  const [hooks, setHooks] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/webhooks").then(({ data }) => setHooks(data));
  useEffect(() => { load(); }, []);
  const create = async (form) => {
    await api.post("/webhooks", form);
    setOpen(false); load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete webhook?")) return;
    await api.delete(`/webhooks/${id}`); load();
  };
  return (
    <>
      <div className="flex justify-end">
        <button data-testid="new-webhook-btn" onClick={() => setOpen(true)} className="span-btn"><Plus size={13}/> New webhook</button>
      </div>
      <div className="card-flat mt-4">
        <table className="span-table">
          <thead><tr><th>URL</th><th>Events</th><th>Status</th><th>Deliveries</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {hooks.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[12px] text-[var(--span-text-2)]">No webhooks.</td></tr>}
            {hooks.map(h => (
              <tr key={h.webhook_id}>
                <td className="font-mono text-[11px] max-w-[300px] truncate">{h.url}</td>
                <td>{(h.events || []).map(e => <span key={e} className="badge mr-1">{e}</span>)}</td>
                <td><span className={`badge ${h.active ? "badge-success" : "badge-danger"}`}>{h.active ? "active" : "paused"}</span></td>
                <td className="font-mono tnum">{h.deliveries || 0}</td>
                <td className="text-[var(--span-text-2)]">{fmtDate(h.created_at)}</td>
                <td><button onClick={() => del(h.webhook_id)} className="span-btn span-btn-ghost h-7 text-[11px]"><Trash size={11}/> Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <NewWebhook onClose={() => setOpen(false)} onCreate={create} />}
    </>
  );
}

function NewWebhook({ onClose, onCreate }) {
  const [f, setF] = useState({ url: "", events: ["escalation.created", "case.closed", "lead.created"], active: true });
  const events = ["escalation.created", "escalation.resolved", "case.created", "case.closed", "lead.created", "lead.converted", "payment.received", "evidence.anchored"];
  const toggle = (e) => setF({ ...f, events: f.events.includes(e) ? f.events.filter(x => x !== e) : [...f.events, e] });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={(e) => { e.preventDefault(); onCreate(f); }} className="card-flat w-full max-w-[520px]">
        <div className="px-5 py-4 border-b border-[var(--span-border)] flex items-center justify-between">
          <div className="font-display font-bold text-[17px]">New webhook</div>
          <button type="button" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label-eyebrow block mb-1.5">Endpoint URL</label>
            <input required type="url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} className="span-input font-mono" placeholder="https://your-system.com/hooks/span" data-testid="webhook-url"/>
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Events</label>
            <div className="grid grid-cols-2 gap-2">
              {events.map(e => (
                <label key={e} className="flex items-center gap-2 text-[12px] cursor-pointer">
                  <input type="checkbox" checked={f.events.includes(e)} onChange={() => toggle(e)} /> <span className="font-mono">{e}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[var(--span-border)] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
          <button className="span-btn" data-testid="submit-webhook">Create webhook</button>
        </div>
      </form>
    </div>
  );
}
